<template>
  <section class="space-y-5">
    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <p class="text-sm font-semibold text-zinc-800">
        Add note
      </p>
      <p class="mt-1 text-xs text-zinc-500">
        Keep internal notes about this member. Notes are creator-only and sync with Chat sticky notes.
      </p>

      <div class="mt-3">
        <textarea
          v-model.trim="newNoteText"
          rows="3"
          maxlength="100"
          placeholder="Write a note..."
          class="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:ring-0"
        />
      </div>

      <div class="mt-2 flex flex-wrap items-center justify-between gap-3">
        <p class="text-xs text-zinc-500">
          {{ newNoteText.length }}/100
        </p>
        <button
          type="button"
          class="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isSaving || !newNoteText"
          @click="createNote"
        >
          {{ isSaving ? 'Saving...' : 'Add Note' }}
        </button>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-600"
    >
      {{ errorMessage }}
    </div>

    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div class="flex items-center justify-between gap-3 border-b border-zinc-200 pb-3">
        <p class="text-sm font-semibold text-zinc-800">
          Notes ({{ notes.length }})
        </p>
        <button
          type="button"
          class="h-9 rounded-lg bg-zinc-100 px-3 text-xs font-semibold text-zinc-700 hover:bg-zinc-200"
          :disabled="isLoading"
          @click="loadNotes"
        >
          {{ isLoading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>

      <div class="pt-4">
        <div
          v-if="isLoading"
          class="rounded-md bg-zinc-50 px-3 py-4 text-sm text-zinc-500"
        >
          Loading notes...
        </div>
        <div
          v-else-if="!notes.length"
          class="rounded-md bg-zinc-50 px-3 py-4 text-sm text-zinc-500"
        >
          No notes added yet.
        </div>

        <div
          v-else
          class="space-y-3"
        >
          <div
            v-for="note in notes"
            :key="`member-note-${note.id}`"
            class="rounded-lg border border-zinc-200 bg-zinc-50 p-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0 flex-1">
                <template v-if="editingNoteId === note.id">
                  <textarea
                    v-model.trim="editingNoteText"
                    rows="2"
                    maxlength="100"
                    class="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:ring-0"
                  />
                  <p class="mt-1 text-xs text-zinc-500">
                    {{ editingNoteText.length }}/100
                  </p>
                </template>
                <template v-else>
                  <p class="whitespace-pre-wrap break-words text-sm text-zinc-800">
                    {{ note.noteText }}
                  </p>
                </template>
              </div>

              <div class="flex items-center gap-2">
                <template v-if="editingNoteId === note.id">
                  <button
                    type="button"
                    class="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isSaving || !editingNoteText"
                    @click="saveNoteEdit(note)"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    class="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                    :disabled="isSaving"
                    @click="cancelEdit"
                  >
                    Cancel
                  </button>
                </template>
                <template v-else>
                  <button
                    type="button"
                    class="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                    :disabled="isSaving"
                    @click="startEdit(note)"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    class="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="isSaving"
                    @click="deleteNote(note)"
                  >
                    Delete
                  </button>
                </template>
              </div>
            </div>

            <p class="mt-2 text-xs text-zinc-500">
              Created {{ formatDateTime(note.createdAt) }}
              <span v-if="note.updatedAt && note.updatedAt !== note.createdAt">
                • Updated {{ formatDateTime(note.updatedAt) }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  memberId: {
    type: [String, Number],
    default: null,
  },
});

const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const notes = ref([]);
const newNoteText = ref('');
const editingNoteId = ref(0);
const editingNoteText = ref('');
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiBackendUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;

const memberIdNumber = () => Number(props.memberId || 0);
const requestBases = computed(() => {
  const rows = Array.from(new Set([apiProxyUrl, apiBackendUrl].map((value) => String(value || '').trim()).filter(Boolean)));
  return rows.length ? rows : [''];
});

const requestNotes = async ({ method = 'GET', noteId = null, body = null, memberId = 0 } = {}) => {
  const paths = [];
  const notePath = noteId ? `/${encodeURIComponent(String(noteId))}` : '';
  if (method === 'GET') {
    const query = new URLSearchParams({ memberId: String(memberId) }).toString();
    paths.push(`/api/creator-member-notes?${query}`);
    paths.push(`/api/member-notes?${query}`);
  } else {
    paths.push(`/api/creator-member-notes${notePath}`);
    paths.push(`/api/member-notes${notePath}`);
  }

  let response = null;
  let payload = {};
  let lastError = '';
  for (const base of requestBases.value) {
    for (const path of paths) {
      response = await fetch(`${base}${path}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body == null ? undefined : JSON.stringify(body),
      });
      payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.success !== false) {
        return payload;
      }
      lastError = String(payload?.message || payload?.error || `Request failed (${response.status})`);
      if (response.status !== 404) break;
    }
    if (response?.ok && payload?.success !== false) break;
  }

  throw new Error(lastError || `Request failed (${response?.status || 500})`);
};

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const loadNotes = async () => {
  const memberId = memberIdNumber();
  if (!Number.isInteger(memberId) || memberId <= 0) {
    notes.value = [];
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';
  try {
    const payload = await requestNotes({ method: 'GET', memberId });
    notes.value = Array.isArray(payload?.notes) ? payload.notes : [];
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed loading notes.');
    notes.value = [];
  } finally {
    isLoading.value = false;
  }
};

const createNote = async () => {
  const memberId = memberIdNumber();
  const noteText = String(newNoteText.value || '').trim().slice(0, 100);
  if (!memberId || !noteText) return;
  isSaving.value = true;
  errorMessage.value = '';
  try {
    await requestNotes({
      method: 'POST',
      memberId,
      body: { memberId, noteText },
    });
    newNoteText.value = '';
    await loadNotes();
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed creating note.');
  } finally {
    isSaving.value = false;
  }
};

const startEdit = (note) => {
  editingNoteId.value = Number(note?.id || 0);
  editingNoteText.value = String(note?.noteText || '').trim();
};

const cancelEdit = () => {
  editingNoteId.value = 0;
  editingNoteText.value = '';
};

const saveNoteEdit = async (note) => {
  const noteId = Number(note?.id || 0);
  const noteText = String(editingNoteText.value || '').trim().slice(0, 100);
  if (!noteId || !noteText) return;
  isSaving.value = true;
  errorMessage.value = '';
  try {
    await requestNotes({
      method: 'PUT',
      noteId,
      body: { noteText },
    });
    cancelEdit();
    await loadNotes();
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed updating note.');
  } finally {
    isSaving.value = false;
  }
};

const deleteNote = async (note) => {
  const noteId = Number(note?.id || 0);
  if (!noteId) return;
  const confirmed = window.confirm('Delete this note?');
  if (!confirmed) return;
  isSaving.value = true;
  errorMessage.value = '';
  try {
    await requestNotes({
      method: 'DELETE',
      noteId,
    });
    if (editingNoteId.value === noteId) {
      cancelEdit();
    }
    await loadNotes();
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed deleting note.');
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => props.memberId,
  () => {
    cancelEdit();
    newNoteText.value = '';
    void loadNotes();
  },
  { immediate: true }
);
</script>
